import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { KgValue } from '@/components/efs/KgValue';
import { cn } from '@/lib/utils';
import type { ImportJobSummary } from '../types';

interface ImportSheetsSummaryProps {
  summary: ImportJobSummary | undefined;
  className?: string;
}

const Tile = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex flex-col gap-1 rounded-md border border-efs-border bg-efs-bg-panel px-3 py-2">
    <span className="text-xs text-efs-text-muted">{label}</span>
    <span className="text-lg font-semibold text-efs-text-primary">{value}</span>
  </div>
);

export const ImportSheetsSummary = ({ summary, className }: ImportSheetsSummaryProps) => {
  const { t } = useTranslation();

  if (!summary) {
    return (
      <div
        className={cn(
          'rounded-md border border-efs-border p-4 text-sm text-efs-text-muted',
          className,
        )}
      >
        {t('efs.imports.wizard.empty_summary', { ns: 'imports' })}
      </div>
    );
  }

  // P1-#8: keys mirror backend `stage_parsed_workbook`. The label keys
  // remain the same so translations don't need to move.
  const blocking = (summary.inbound_errors ?? 0) + (summary.dismantling_errors ?? 0);
  const warnings = summary.workbook_warnings ?? 0;
  const info = summary.category_mismatches ?? 0;

  return (
    <div className={cn('grid grid-cols-2 gap-3 md:grid-cols-4', className)}>
      <Tile
        label={t('efs.imports.summary.inbound_rows', { ns: 'imports' })}
        value={summary.inbound_rows ?? '—'}
      />
      <Tile
        label={t('efs.imports.summary.dismantling_rows', { ns: 'imports' })}
        value={summary.dismantling_rows ?? '—'}
      />
      <Tile
        label={t('efs.imports.summary.inbound_total_kg', { ns: 'imports' })}
        value={summary.inbound_total_kg ? <KgValue value={summary.inbound_total_kg} /> : '—'}
      />
      <Tile
        label={t('efs.imports.summary.dismantling_total_kg', { ns: 'imports' })}
        value={
          summary.dismantling_total_kg ? <KgValue value={summary.dismantling_total_kg} /> : '—'
        }
      />
      <Tile
        label={t('efs.imports.summary.blocking_error_count', { ns: 'imports' })}
        value={
          <span className={cn(blocking > 0 ? 'text-efs-error' : 'text-efs-text-primary')}>
            {blocking}
          </span>
        }
      />
      <Tile
        label={t('efs.imports.summary.warning_count', { ns: 'imports' })}
        value={
          <span className={cn(warnings > 0 ? 'text-efs-warning' : 'text-efs-text-primary')}>
            {warnings}
          </span>
        }
      />
      <Tile label={t('efs.imports.summary.info_count', { ns: 'imports' })} value={info} />
    </div>
  );
};
